// src/components/admin/NaicsCategoryPicker.tsx
// Categorized, searchable, multi-select NAICS picker for the SAM.gov importer.
// Replaces the old hardcoded 28-chip list. Loads every code from the `naics_codes`
// table and groups it under the 20 OFFICIAL NAICS sectors, derived deterministically
// from each code's 2-digit prefix (so the DB's inconsistent/invented sector labels
// don't matter). Sectors are collapsible; search matches code or title and
// auto-expands the matching sectors.

import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, Tag, Check, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface NaicsRow { code: string; title: string; }

// Official NAICS 2-digit sectors. The map key is the 2-digit prefix; 31-33, 44-45,
// and 48-49 share one sector each.
const SECTOR_BY_PREFIX: Record<string, string> = {
  '11': 'Agriculture, Forestry, Fishing and Hunting',
  '21': 'Mining, Quarrying, and Oil and Gas Extraction',
  '22': 'Utilities',
  '23': 'Construction',
  '31': 'Manufacturing', '32': 'Manufacturing', '33': 'Manufacturing',
  '42': 'Wholesale Trade',
  '44': 'Retail Trade', '45': 'Retail Trade',
  '48': 'Transportation and Warehousing', '49': 'Transportation and Warehousing',
  '51': 'Information',
  '52': 'Finance and Insurance',
  '53': 'Real Estate and Rental and Leasing',
  '54': 'Professional, Scientific, and Technical Services',
  '55': 'Management of Companies and Enterprises',
  '56': 'Administrative, Support, Waste Management & Remediation',
  '61': 'Educational Services',
  '62': 'Health Care and Social Assistance',
  '71': 'Arts, Entertainment, and Recreation',
  '72': 'Accommodation and Food Services',
  '81': 'Other Services (except Public Administration)',
  '92': 'Public Administration',
};

// Government-contracting sectors floated to the top; everything else follows.
const SECTOR_ORDER = [
  'Professional, Scientific, and Technical Services',
  'Administrative, Support, Waste Management & Remediation',
  'Information',
  'Construction',
  'Manufacturing',
  'Educational Services',
  'Health Care and Social Assistance',
  'Transportation and Warehousing',
  'Finance and Insurance',
  'Public Administration',
  'Utilities',
  'Real Estate and Rental and Leasing',
  'Wholesale Trade',
  'Retail Trade',
  'Other Services (except Public Administration)',
  'Management of Companies and Enterprises',
  'Mining, Quarrying, and Oil and Gas Extraction',
  'Agriculture, Forestry, Fishing and Hunting',
  'Arts, Entertainment, and Recreation',
  'Accommodation and Food Services',
];

function sectorFor(code: string): string {
  return SECTOR_BY_PREFIX[code.slice(0, 2)] || 'Other';
}

interface Props {
  selected: string[];
  onToggle: (code: string) => void;
}

export function NaicsCategoryPicker({ selected, onToggle }: Props) {
  const [rows, setRows] = useState<NaicsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openSectors, setOpenSectors] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('naics_codes')
          .select('code, title')
          .order('code')
          .limit(5000); // full taxonomy is ~1,012 rows; default cap is 1,000
        if (error) throw error;
        if (!cancelled) setRows((data || []).filter((r: any) => /^\d{6}$/.test(r.code)));
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load NAICS codes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const q = search.trim().toLowerCase();

  // Group filtered codes by official sector.
  const grouped = useMemo(() => {
    const map = new Map<string, NaicsRow[]>();
    for (const r of rows) {
      if (q && !r.code.includes(q) && !r.title.toLowerCase().includes(q)) continue;
      const sec = sectorFor(r.code);
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec)!.push(r);
    }
    const ordered: Array<{ sector: string; codes: NaicsRow[] }> = [];
    for (const sec of SECTOR_ORDER) {
      if (map.has(sec)) { ordered.push({ sector: sec, codes: map.get(sec)! }); map.delete(sec); }
    }
    for (const [sec, codes] of map) ordered.push({ sector: sec, codes }); // any leftovers
    return ordered;
  }, [rows, q]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const isOpen = (sec: string) => !!q || openSectors.has(sec); // searching auto-expands
  const toggleSector = (sec: string) =>
    setOpenSectors(prev => { const n = new Set(prev); n.has(sec) ? n.delete(sec) : n.add(sec); return n; });

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-bw-muted border rounded-lg bg-bw-surface-subtle">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading NAICS codes…
      </div>
    );
  }
  if (error) {
    return <div className="p-3 text-sm text-urgent border border-red-200 rounded-lg bg-urgent-bg">Couldn't load NAICS codes: {error}</div>;
  }

  return (
    <div>
      <div className="relative mb-2">
        <Search className="h-4 w-4 text-bw-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search NAICS code or title (e.g. 541512 or cyber)…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-bw-border rounded-lg bg-bw-surface focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
        />
      </div>

      {/* Selected summary */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <span className="text-xs text-bw-muted">Selected ({selected.length}):</span>
          {selected.slice(0, 12).map(code => (
            <button
              key={code}
              onClick={() => onToggle(code)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-bw-primary text-white"
              title="Click to remove"
            >
              {code} ✕
            </button>
          ))}
          {selected.length > 12 && <span className="text-xs text-bw-muted">+{selected.length - 12} more</span>}
        </div>
      )}

      <div className="max-h-80 overflow-y-auto border rounded-lg bg-bw-surface-subtle divide-y divide-bw-border">
        {grouped.length === 0 && (
          <p className="p-3 text-sm text-bw-muted">No NAICS codes match "{search}".</p>
        )}
        {grouped.map(({ sector, codes }) => {
          const selCount = codes.filter(c => selectedSet.has(c.code)).length;
          return (
            <div key={sector}>
              <button
                onClick={() => toggleSector(sector)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-bw-surface"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-bw-ink">
                  {isOpen(sector) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Tag className="h-3.5 w-3.5 text-bw-muted" />
                  {sector}
                </span>
                <span className="text-xs text-bw-muted">
                  {selCount > 0 && <span className="text-bw-primary font-semibold mr-1">{selCount}/</span>}{codes.length}
                </span>
              </button>
              {isOpen(sector) && (
                <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                  {codes.map(c => {
                    const on = selectedSet.has(c.code);
                    return (
                      <button
                        key={c.code}
                        onClick={() => onToggle(c.code)}
                        title={c.title}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                          on ? 'bg-bw-primary text-white' : 'bg-bw-surface border border-bw-border text-bw-ink hover:border-servet-blue'
                        }`}
                      >
                        {on && <Check className="h-3 w-3" />}
                        <span className="font-mono">{c.code}</span>
                        <span className="text-[11px] opacity-80 max-w-[160px] truncate">{c.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-bw-muted mt-1">
        {rows.length} codes across {new Set(rows.map(r => sectorFor(r.code))).size} sectors. Search by code or keyword; click a sector to expand.
      </p>
    </div>
  );
}
